import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useRuntimeConfig } from 'config/useRuntimeConfig';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { closeAllModals } from 'reducers/modalReducer';
import { LoadingButton } from '@mui/lab';
import { JBAlert, JBColors, JBIcon } from '@bjb-gcrm/gcrm-frontend-components';
import { BP_TOOL_URL } from 'services/constants';
import { useCallBrBusinessEvent } from 'hooks/queries/useDisableBr';
import { BrBusinessEvents } from 'types/BrTypes';
import DisableBrOpenTasksModal from 'views/BrTabs/DisableBrOpenTasksModal';
import ResponsiveDialog from 'components/Common/ResponsiveDialog';
import { useFetchBrOpenTasks } from 'hooks/queries/useFetchBrOpenTasks';

const styles = {
  checkbox: {
    padding: '0 10px 10px 10px',
  },
  formControlLabelListItem: {
    display: 'list-item',
    listStyleType: 'square',
    fontSize: '12px',
    fontWeight: 'normal',
    color: 'black',
    lineHeight: 1,
  },
};

const DisableBrModal = ({ brId }: { brId: string }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const confirmDeactivateStatusList = t('accounts.tabs.more.deactivate.modal.confirmStatusTextItems', {
    returnObjects: true,
  });
  const confirmDeactivateStatusItems = Array.isArray(confirmDeactivateStatusList)
    ? confirmDeactivateStatusList.filter((item): item is string => typeof item === 'string')
    : [];
  const [deactivateConfirmed, setDeactivateConfirmed] = useState<boolean>(false);
  const [deactivateStatusConfirmed, setDeactivateStatusConfirmed] = useState<boolean>(false);
  const disableBr = useCallBrBusinessEvent(brId);
  const [brHasTasksNotComplete, setBrHasTasksNotComplete] = useState<boolean>(true);
  const runtimeConfig = useRuntimeConfig();
  const isCheckForTasksEnabled = runtimeConfig.configData.featureFlags?.openTaskCheck?.enabled;
  const { data: taskStatusResponse, isFetching, isError } = useFetchBrOpenTasks(brId, isCheckForTasksEnabled);
  useEffect(() => {
    if (!isFetching && (taskStatusResponse?.tasks === undefined || taskStatusResponse?.tasks.length === 0)) {
      setBrHasTasksNotComplete(false);
    }
  }, [taskStatusResponse]);

  return (
    <>
      {!isFetching && brHasTasksNotComplete && <DisableBrOpenTasksModal />}
      {!isFetching && !brHasTasksNotComplete && !isError && (
        <ResponsiveDialog id="deactivate-br-modal">
          <DialogContent>
            <DialogTitle id="disable-modal-title" sx={{ padding: 0 }}>
              <Grid container justifyContent="space-between">
                <Grid item container xs={11} alignItems="center">
                  <JBIcon iconName="dangerOutline" size="xl" fill={JBColors.support.orange[120]} />
                  <Typography variant="h5" display="inline" ml="16px" color={JBColors.support.orange[120]}>
                    {t('accounts.tabs.more.deactivate.modal.title', 'BR deactivation confirmation')}
                  </Typography>
                </Grid>

                <Grid item xs={1}>
                  <IconButton onClick={() => dispatch(closeAllModals())}>
                    <JBIcon iconName="close" fill={JBColors.support.orange[120]} size="xl" />
                  </IconButton>
                </Grid>
              </Grid>
              <Divider sx={{ borderColor: JBColors.support.orange[120] }} />
            </DialogTitle>

            <FormGroup sx={{ marginTop: '16px' }}>
              <FormControlLabel
                control={<Checkbox value={deactivateConfirmed} sx={styles.checkbox} />}
                onChange={() => setDeactivateConfirmed(!deactivateConfirmed)}
                sx={{ alignItems: 'flex-start' }}
                label={
                  <>
                    {t('accounts.tabs.more.deactivate.modal.confirmText', 'Hereby I confirm:')}
                    <List sx={{ pl: 2 }}>
                      <ListItem sx={styles.formControlLabelListItem}>
                        <Trans
                          i18nKey="accounts.tabs.more.deactivate.modal.confirmTextItem1"
                          t={t}
                          components={[<Link href={BP_TOOL_URL} target="_blank" />]}
                        />
                      </ListItem>
                      <ListItem sx={styles.formControlLabelListItem}>
                        {t('accounts.tabs.more.deactivate.modal.confirmTextItem2')}
                      </ListItem>
                      <ListItem sx={styles.formControlLabelListItem}>
                        {t('accounts.tabs.more.deactivate.modal.confirmTextItem3')}
                      </ListItem>
                      <ListItem sx={styles.formControlLabelListItem}>
                        {t('accounts.tabs.more.deactivate.modal.confirmTextItem4')}
                      </ListItem>
                    </List>
                  </>
                }
              />

              <FormControlLabel
                control={<Checkbox value={deactivateStatusConfirmed} sx={styles.checkbox} />}
                sx={{ alignItems: 'flex-start' }}
                onChange={() => setDeactivateStatusConfirmed(!deactivateStatusConfirmed)}
                label={
                  <>
                    <Trans i18nKey="accounts.tabs.more.deactivate.modal.confirmText" t={t} />
                    <List sx={{ pl: 2 }}>
                      {confirmDeactivateStatusItems.map((text, index) => (
                        <ListItem sx={styles.formControlLabelListItem} key={index}>
                          {text}
                        </ListItem>
                      ))}
                    </List>
                  </>
                }
              />
            </FormGroup>
            <Box sx={{ marginTop: '8px' }}>
              <JBAlert color="warning">
                <Typography variant="body1" color={JBColors.support.orange[130]}>
                  <strong>{t('accounts.tabs.more.deactivate.modal.confirmTextAlertAttention', 'Attention')}</strong>
                </Typography>
                {t(
                  'accounts.tabs.more.deactivate.modal.confirmTextAlert',
                  'After confirmation and deactivation of the BR, it cannot be reactivated.',
                )}
              </JBAlert>
            </Box>
            <DialogActions>
              <Button
                onClick={() => dispatch(closeAllModals())}
                color="primary"
                id="deactivate-br-cancel-button"
                disabled={disableBr.isPending}
              >
                Cancel
              </Button>

              <LoadingButton
                variant="contained"
                color="primary"
                disabled={!deactivateConfirmed || !deactivateStatusConfirmed || disableBr.isPending}
                type="submit"
                loading={disableBr.isPending}
                onClick={() =>
                  disableBr.mutate({
                    brUid: brId,
                    businessEvent: BrBusinessEvents.BR_DEACTIVATED,
                  })
                }
              >
                {t('accounts.tabs.more.deactivate', 'Deactivate BR')}
              </LoadingButton>
            </DialogActions>
          </DialogContent>
        </ResponsiveDialog>
      )}
    </>
  );
};

export default DisableBrModal;
